import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { AdminSupportExtra, InsertSupportExtraBody, UpdateSupportExtraBody } from "./types/supportExtras";

export const useSupportExtrasApi = () => {
  const api = useApi();

  const useAdminSupportExtras = useCallback(
    (query: PaginationQuery, signal: AbortSignal, systemId?: string) => {
      const mappingUrl = `/admin/settings/patrol/support_extras`;
      const systemIdUrl = `/admin/settings/patrol/systems/${systemId}/support_extras`;
      const url = buildUrl(systemId ? systemIdUrl : mappingUrl, query);
      return useSWR<Paginated<AdminSupportExtra>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminSupportExtra>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminSupportExtra = useCallback(
    async (body: InsertSupportExtraBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/systems/${body.systemId}/support_extras`;
      const id = await api.post<{ id: string }, InsertSupportExtraBody>(url, body);
      mutateCache([
        `/admin/settings/patrol/support_optionals`,
        `/admin/settings/patrol/systems/${body.systemId}/support_optionals`,
        `/admin/settings/patrol/support_extras`,
        `/admin/settings/patrol/systems/${body.systemId}/support_extras`,
      ]);
      return id;
    },
    [api],
  );

  const updateAdminSupportExtra = useCallback(
    async (extraId: string, body: UpdateSupportExtraBody): Promise<void> => {
      const url = `/admin/settings/patrol/systems/${body.systemId}/support_extras/${extraId}`;
      await api.put<UpdateSupportExtraBody>(url, body);
      mutateCache([
        `/admin/settings/patrol/support_optionals`,
        `/admin/settings/patrol/systems/${body.systemId}/support_optionals`,
        `/admin/settings/patrol/support_extras`,
        `/admin/settings/patrol/systems/${body.systemId}/support_extras`,
      ]);
    },
    [api],
  );

  const deleteAdminSupportExtra = useCallback(
    async (extraId: string, systemId: string): Promise<void> => {
      await api.del(`/admin/settings/patrol/systems/${systemId}/support_extras/${extraId}`);
      mutateCache([
        `/admin/settings/patrol/support_extras`,
        `/admin/settings/patrol/support_extras/${extraId}`,
        `/admin/settings/patrol/systems/${systemId}/support_extras`,
        `/admin/settings/patrol/systems/${systemId}/support_extras/${extraId}`,
        `/admin/settings/patrol/support_optionals`,
        `/admin/settings/patrol/systems/${systemId}/support_optionals`,
      ]);
    },
    [api],
  );

  return {
    useAdminSupportExtras,
    insertAdminSupportExtra,
    updateAdminSupportExtra,
    deleteAdminSupportExtra,
  };
};
